import { useCallback, useEffect, useRef, useState } from "react";

import { useAppContext } from "../../../hooks/useAppContext";
import { useMessage } from "../../../hooks/useMessage";
import { useSelectedZone } from "../../../hooks/useSelection";
import { useTextZoneDashboard } from "../../../hooks/useTextZoneDashboard";
import { useMaxTextLength } from "./use-max-text-length";

export function useTextZoneTextValue() {
	const selectedZone = useSelectedZone();
	const dashboard = useTextZoneDashboard();
	const { editor, platform } = useAppContext();
	const textZone = eXpress.core.isAnyTextZone(selectedZone) ? selectedZone : undefined;
	const [textValue, setTextValue] = useState("");
	const [placeholderText, setPlaceholderText] = useState(
		textZone ? textZone.placeholderText : "",
	);
	const [canEdit, setCanEdit] = useState(false);
	const [initialFontSizeInPt, setInitialFontSizeInPt] = useState(
		textZone?.textSettings.ptFontSize,
	);

	const inputRef = useRef<HTMLTextAreaElement>(null);
	const maxTextLength = useMaxTextLength();
	const maxLinesAllowed = textZone?.maxNumberOfLines ?? 0;
	const defaultPlaceholderText = textZone?.getPlaceholderTexts(true).at(0) ?? "";

	const updateState = useCallback(
		(forTextZone: eXpress.core.AnyTextZone | undefined) => {
			const newText = forTextZone ? forTextZone.text : "";
			setTextValue(newText);
			setPlaceholderText(forTextZone ? forTextZone.placeholderText : "");
			setCanEdit(dashboard.canSetTextValue(forTextZone));

			// Mac/WebKit bug: when the textarea value is programmatically cleared to "",
			// the browser clips the placeholder to the width of the previously-rendered text.
			// Forcing a repaint after React commits the empty value fixes this.
			if (newText === "" && inputRef.current && platform.browserInfo.isMac()) {
				const el = inputRef.current;
				requestAnimationFrame(() => {
					el.style.transform = "translateZ(0)";
					requestAnimationFrame(() => {
						el.style.transform = "";
					});
				});
			}
		},
		[dashboard, platform.browserInfo],
	);

	useMessage(
		useCallback(() => {
			const selectedZone2 = editor.selection.getCurrentZone();
			const textZone2 = eXpress.core.isAnyTextZone(selectedZone2) ? selectedZone2 : undefined;
			updateState(textZone2);
		}, [editor.selection, updateState]),
	);

	useEffect(() => {
		updateState(textZone);
		setInitialFontSizeInPt(textZone?.textSettings.ptFontSize);
	}, [textZone, updateState]);

	function updateText(
		requestedValue: string,
		options?: {
			maxReplaceDelay?: number;
		},
	) {
		const originalValue = textZone?.text || "";
		let skipCursorFix = false;
		if (eXpress.core.text.getNumberOfChars(originalValue) > maxTextLength) {
			skipCursorFix = true;
		}

		if (
			!skipCursorFix &&
			maxTextLength &&
			eXpress.core.text.getNumberOfChars(requestedValue) > maxTextLength
		) {
			const cursorStart = inputRef.current?.selectionStart;
			setTimeout(() => {
				if (cursorStart)
					inputRef?.current?.setSelectionRange(cursorStart - 1, cursorStart - 1);
			}, 0);
			return false;
		}

		const strippedAndReplacedText = dashboard.stripUnsupportedCharacters(
			requestedValue,
			textZone?.definition?.supportedCharSet,
			maxLinesAllowed,
			maxTextLength,
		).newText;

		if (textZone && dashboard.canSetTextValue(textZone)) {
			dashboard.setTextValue(textZone, strippedAndReplacedText, {
				maxReplaceDelay: options?.maxReplaceDelay, // defaults to 2000 ms
				preferedFontSizeInPt: initialFontSizeInPt,
			});
			setTextValue(strippedAndReplacedText);
			return true;
		}

		return false;
	}

	return {
		dashboard,
		textZone,
		textValue,
		placeholderText,
		canEdit,
		maxTextLength,
		inputRef,
		updateText,
		defaultPlaceholderText,
	};
}
